import { useEffect, useState } from "react";
import { api } from "../utils/api";
import ProductCard from "../components/ProductCard";
import { Link } from "react-router-dom";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeatured();
  }, []);

  const fetchFeatured = async () => {
    setLoading(true);
    try {
      const data = await api.getFeatured();
      setFeatured(data || []);
    } catch (error) {
      console.error("Error fetching featured products:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-20">

      {/* ------------------- HERO SECTION ------------------- */}
      <section className="relative h-72 md:h-96 rounded-xl overflow-hidden shadow-xl">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/70 to-pink-500/70 flex flex-col justify-center items-center text-center p-6 text-white z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold drop-shadow-lg">
            Handmade With Love ❤️
          </h1>
          <p className="mt-3 text-lg md:text-xl opacity-90">
            Beautiful handcrafted products made just for you.
          </p>

          <Link
            to="/products"
            className="mt-5 bg-white text-purple-700 font-semibold px-6 py-3 rounded-full shadow-lg hover:bg-gray-100 transition"
          >
            Shop Now →
          </Link>
        </div>

        {/* Banner image */}
        <img
          src="https://images.pexels.com/photos/1050244/pexels-photo-1050244.jpeg"
          alt="Handmade Banner"
          className="w-full h-full object-cover"
        />
      </section>

      {/* ------------------- FEATURED PRODUCTS ------------------- */}
      <section className="max-w-6xl mx-auto px-2">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">
            Featured Products ✨
          </h2>
          <Link
            to="/products"
            className="text-purple-600 hover:text-purple-700 font-medium text-sm"
          >
            View All →
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading featured products...</p>
          </div>
        ) : featured.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600 text-lg mb-4">
              No featured products yet! 
            </p>
            <p className="text-gray-500 text-sm">
              Check back soon or browse all products
            </p>
            <Link
              to="/products"
              className="inline-block mt-4 bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {featured.map((p) => (
              <ProductCard key={p._id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* ------------------- CATEGORY SHOWCASE ------------------- */}
      <section className="max-w-6xl mx-auto px-2">
        <h2 className="text-3xl font-bold mb-6 text-gray-800">
          Explore Categories 🧵
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { name: "Frames", img: "https://images.pexels.com/photos/1109543/pexels-photo-1109543.jpeg" },
            { name: "Clay Art", img: "https://images.pexels.com/photos/102129/pexels-photo-102129.jpeg" },
            { name: "Bouquets", img: "https://images.pexels.com/photos/931167/pexels-photo-931167.jpeg" },
            { name: "Accessories", img: "https://images.pexels.com/photos/145661/pexels-photo-145661.jpeg" },
          ].map((cat) => (
            <Link
              key={cat.name}
              to="/products"
              className="relative h-36 rounded-xl overflow-hidden shadow-lg hover:scale-105 transition duration-300"
            >
              <img
                src={cat.img}
                alt={cat.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-lg font-semibold">
                {cat.name}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ------------------- TESTIMONIALS ------------------- */}
      <section className="max-w-6xl mx-auto px-2">
        <h2 className="text-3xl font-bold mb-6 text-gray-800">
          What Customers Say ❤️
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: "Aditi", msg: "Beautiful handmade clay items! Quality is amazing." },
            { name: "Rahul", msg: "Bought a customised frame — absolutely loved it!" },
            { name: "Sneha", msg: "The bouquets are unique and perfect for gifting!" },
          ].map((t, i) => (
            <div
              key={i}
              className="p-6 bg-white border rounded-xl shadow-md hover:shadow-lg transition"
            >
              <p className="text-gray-700 italic">"{t.msg}"</p>
              <h3 className="mt-4 font-bold text-gray-900">— {t.name}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------- WHY CHOOSE US ------------------- */}
      <section className="max-w-6xl mx-auto px-2">
        <h2 className="text-3xl font-bold mb-6 text-gray-800">
          Why Choose Our Handmade Store? ⭐
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: "100% Handmade",
              desc: "Each product is crafted with love and care."
            },
            {
              title: "Customizable Gifts",
              desc: "Personalize your gifts for every special moment."
            },
            {
              title: "Premium Quality",
              desc: "We use high-quality materials to ensure durability."
            }
          ].map((item, i) => (
            <div key={i} className="p-6 bg-purple-50 rounded-xl text-center shadow-sm hover:shadow-md transition">
              <h3 className="text-xl font-semibold">{item.title}</h3>
              <p className="text-gray-700 mt-2">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}